<template>
  <div class="otp" :class="{ shake: error }" role="group" aria-label="4-digit code">
    <input
      v-for="(digit, i) in digits"
      :key="i"
      :ref="(el) => (inputs[i] = el)"
      class="otp__box"
      :class="{ 'otp__box--filled': digit, 'otp__box--error': error }"
      type="tel"
      inputmode="numeric"
      autocomplete="one-time-code"
      :value="digit"
      :aria-label="`Digit ${i + 1}`"
      @input="onInput(i, $event)"
      @keydown="onKeydown(i, $event)"
      @paste="onPaste"
      @focus="$event.target.select()"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'

const props = defineProps({
  modelValue: { type: String, default: '' },
  length: { type: Number, default: 4 },
  error: { type: Boolean, default: false },
  autofocus: { type: Boolean, default: true }
})
const emit = defineEmits(['update:modelValue', 'complete'])

const digits = ref(Array.from({ length: props.length }, (_, i) => props.modelValue[i] || ''))
const inputs = ref([])

watch(() => props.modelValue, (value) => {
  if (value === digits.value.join('')) return
  digits.value = Array.from({ length: props.length }, (_, i) => value[i] || '')
  // Cleared from outside (wrong code): start again from the first box.
  if (!value) inputs.value[0]?.focus()
})

function commit() {
  const value = digits.value.join('')
  emit('update:modelValue', value)
  if (value.length === props.length) emit('complete', value)
}

// A box may receive several digits at once (SMS autofill, fast typing): spread them forward.
function onInput(i, event) {
  const previous = digits.value[i]
  let value = event.target.value.replace(/\D/g, '')
  if (value.length >= props.length) {
    digits.value = value.slice(0, props.length).split('')
    event.target.value = digits.value[i]
    inputs.value[props.length - 1]?.focus()
    commit()
    return
  }
  if (previous && value.length > 1 && value.startsWith(previous)) value = value.slice(previous.length)
  if (!value) {
    digits.value[i] = ''
    event.target.value = ''
    commit()
    return
  }
  const chars = value.slice(0, props.length - i).split('')
  chars.forEach((char, offset) => { digits.value[i + offset] = char })
  event.target.value = digits.value[i]
  inputs.value[Math.min(i + chars.length, props.length - 1)]?.focus()
  commit()
}

function onKeydown(i, event) {
  if (event.key === 'Backspace' && !digits.value[i] && i > 0) {
    digits.value[i - 1] = ''
    inputs.value[i - 1]?.focus()
    commit()
  }
  if (event.key === 'ArrowLeft' && i > 0) inputs.value[i - 1]?.focus()
  if (event.key === 'ArrowRight' && i < props.length - 1) inputs.value[i + 1]?.focus()
}

function onPaste(event) {
  const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, props.length)
  if (!pasted) return
  event.preventDefault()
  digits.value = Array.from({ length: props.length }, (_, i) => pasted[i] || '')
  inputs.value[Math.min(pasted.length, props.length - 1)]?.focus()
  commit()
}

onMounted(() => {
  if (props.autofocus) inputs.value[0]?.focus()
})
</script>

<style scoped>
.otp {
  display: flex;
  gap: 10px;
}

.otp__box {
  flex: 1;
  min-width: 0;
  height: 56px;
  text-align: center;
  font-family: var(--font-display);
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text);
  background: var(--color-field);
  border: 1.5px solid var(--color-field-border);
  border-radius: var(--radius-sm);
  caret-color: var(--color-primary);
  transition: border-color var(--transition-fast);
}

.otp__box:focus {
  border-color: var(--color-primary);
}

/* The accent border is the focus indicator; the global focus ring would draw a box around the box. */
.otp__box:focus-visible {
  outline: none;
}

.otp__box--filled {
  border-color: rgba(255, 255, 255, 0.6);
}

.otp__box--error {
  border-color: var(--color-error);
}
</style>
